import { sectionRegistry } from '../canvas/sectionRegistry';
import {
  autoBodyLeadingRatio,
  type SectionInstance,
  type SettingField,
  type SettingsValues,
  type SettingValue,
  type VisibleWhen,
} from '@aquilixshop/ui-react';
import { useBuilderStore } from '../state/builderStore';
import { useStoreIsInCartMode } from '../state/cartMode';
import { FieldControl } from './FieldControl';
import { FieldTargetProvider } from './fieldTarget';
import { DesktopIcon, MobileIcon } from '../ui/icons';
import { bt, groupLabel, translateFields } from '../i18n';

/** Marker for the fields that declare no `group` — they render directly in "Settings". */
export const DEFAULT_GROUP = '__default__';

/** A `visibleWhen` clause value matches when the field equals the scalar, or is one of the array. */
function clauseMatches(
  value: SettingValue | undefined,
  target: string | number | boolean | Array<string | number | boolean>,
): boolean {
  return Array.isArray(target) ? target.some((t) => t === value) : value === target;
}

/**
 * Reduce a stored value to what a `visibleWhen` clause compares against. Two object shapes would
 * otherwise never match a scalar clause:
 *  - richtext ({ format, html }) — leaving every `{ field: <richtext>, notEquals: '' }` clause permanently
 *    dead. Collapse it to its tag-stripped text so those clauses mean "has any text", as their schemas intend.
 *  - a picker REF ({ kind:'product'|'collection', id }) — collapse it to the referenced id, so `empty`
 *    (and an explicit id comparison) reads a picker exactly like a text field.
 * Scalars pass through.
 */
function comparable(value: SettingValue | undefined): SettingValue | undefined {
  if (value && typeof value === 'object' && 'html' in (value as Record<string, unknown>)) {
    return String((value as { html?: unknown }).html ?? '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  }
  if (value && typeof value === 'object' && 'kind' in (value as Record<string, unknown>)) {
    return String((value as { id?: unknown }).id ?? '');
  }
  return value;
}

/** "Nothing chosen": unset, null, or the empty string a cleared control / cleared ref collapses to. */
function isEmptyValue(value: SettingValue | undefined): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * One `visibleWhen` clause, evaluated against the live settings. All present keys must hold.
 *
 * `sectionSettings` is the PARENT SECTION's settings, passed only by the block inspector: a clause marked
 * `scope: 'section'` reads THOSE instead of the block's own, which is how a block field hides when the
 * section makes it meaningless (comparison table: no "Competitor 2" tick while one column is displayed).
 * Absent (section form) it falls back to the same map — a section field's scope IS the section.
 */
function clauseHolds(
  clause: VisibleWhen,
  settings: SettingsValues,
  sectionSettings?: SettingsValues,
): boolean {
  const source = clause.scope === 'section' ? (sectionSettings ?? settings) : settings;
  const value = comparable(source[clause.field]);
  if (clause.equals !== undefined && !clauseMatches(value, clause.equals)) return false;
  if (clause.notEquals !== undefined && clauseMatches(value, clause.notEquals)) return false;
  if (clause.empty !== undefined && isEmptyValue(value) !== clause.empty) return false;
  return true;
}

/**
 * A field is visible unless its `visibleWhen` predicate fails. A clause mirrors Shopify `visible_if`:
 * `equals` (value equals the scalar OR is one of the array), `notEquals` (value is not the scalar / not one
 * of the array) and/or `empty` (the value is / isn't set — the only clause that can read an object-valued
 * control such as a product picker); when several are present ALL must hold. An ARRAY of clauses is an AND
 * across DIFFERENT sibling fields. Pure + presentational — a hidden field keeps its stored value; we just
 * don't render its control (deep-config infra).
 */
export function isFieldVisible(
  field: SettingField,
  settings: SettingsValues,
  sectionSettings?: SettingsValues,
): boolean {
  const w = field.visibleWhen;
  if (!w) return true;
  const clauses = Array.isArray(w) ? w : [w];
  return clauses.every((c) => clauseHolds(c, settings, sectionSettings));
}

/**
 * Partition fields into ordered groups keyed by `field.group` (ungrouped → DEFAULT_GROUP).
 * Group ORDER follows first appearance in the schema's `fields[]`, so the inspector layout is a
 * deterministic function of the schema (no alphabetical surprise). Returns `[groupName, fields][]`.
 */
export function groupFields(fields: SettingField[]): Array<[string, SettingField[]]> {
  const order: string[] = [];
  const byGroup = new Map<string, SettingField[]>();
  for (const f of fields) {
    const key = f.group ?? DEFAULT_GROUP;
    if (!byGroup.has(key)) {
      byGroup.set(key, []);
      order.push(key);
    }
    byGroup.get(key)!.push(f);
  }
  return order.map((key) => [key, byGroup.get(key)!]);
}

/**
 * THE TWO GROUPS THAT NAME A READER, and what each one is (18/09/2026).
 *
 * A group is just a string, so nothing in the inspector could tell "Mobile" from "Colour" — they drew the
 * same thin heading, and the founder's report was exactly that: the per-device settings existed and could
 * not be FOUND. These two get the device's own glyph, the same one the topbar's preview toggle carries, so
 * "the phone's settings" reads as one part of the panel instead of one heading among nine.
 *
 * Keyed on the RAW (English) group name on purpose: `groupFields` partitions before translation, and the
 * schema's group string is the stable thing. Add a section to the device layout and it is styled for free.
 */
const DEVICE_GROUPS: Readonly<Record<string, 'desktop' | 'mobile'>> = {
  Desktop: 'desktop',
  Mobile: 'mobile',
};

/**
 * What a field carrying `inheritFrom` paints while it holds no value of its own: the sibling's stored
 * value, else that sibling's own schema default. Resolved HERE, where the whole field list and the whole
 * settings object are in hand — the control only ever sees one field.
 */
export function inheritedValue(
  field: SettingField,
  fields: readonly SettingField[],
  settings: SettingsValues,
): SettingValue | undefined {
  // `autoFromSize` : l'état « automatique » est une COURBE, pas un nombre. La poignée doit démarrer sur
  // ce que la courbe peint À LA TAILLE DU MOMENT, sinon la reprise fait sauter le texte sous la main de la
  // vendeuse (interligne d'un bloc « Texte » : 1,80 à 14 px, 1,42 à 24 px). Arrondi au cran du curseur —
  // le décalage restant vaut moins d'un demi-pixel de ligne.
  const fromSize = (field as { autoFromSize?: string }).autoFromSize;
  if (fromSize !== undefined) {
    const sizeField = fields.find((f) => f.id === fromSize) as
      | { default?: SettingValue }
      | undefined;
    const raw = settings[fromSize] ?? sizeField?.default;
    if (typeof raw !== 'number') return undefined;
    const step = (field as { step?: number }).step ?? 0.05;
    return Math.round(autoBodyLeadingRatio(raw) / step) * step;
  }

  const from = (field as { inheritFrom?: string }).inheritFrom;
  if (from === undefined) return undefined;
  const stored = settings[from];
  if (stored !== undefined) return stored;
  const sibling = fields.find((f) => f.id === from) as { default?: SettingValue } | undefined;
  return sibling?.default;
}

/** The device glyph in front of a "Desktop" / "Mobile" heading — decorative, the heading says the word. */
export function DeviceGlyph({ device }: { device: 'desktop' | 'mobile' }) {
  return (
    <span className="bld-grp__device" aria-hidden="true">
      {device === 'mobile' ? <MobileIcon /> : <DesktopIcon />}
    </span>
  );
}

/**
 * The ONE header action a cart-mode store does not get to switch off.
 *
 * "Cart mode" is the STORE-wide question answered in `state/cartMode.ts` (founder rule 16/09, widened to
 * « Buy now » on 05/10/2026): does any buy-box, on any page, put the shopper in a cart? In cart mode the
 * buyer fills a cart and `/cart` is the only road on: hide the icon and they are stranded, and nothing on
 * this screen would say so. The switch shows ON and LOCKED with the reason under it, and the header renders
 * the icon whatever the document stored (`sections/header/Component.tsx`), off the same function.
 */
function isCartLocked(sectionType: string, field: SettingField, cartMode: boolean): boolean {
  return cartMode && sectionType === 'header' && field.id === 'showCart';
}

/**
 * Replace a locked field's helper text with the REASON it is locked. The schema's own `info` describes a
 * choice the seller no longer has here, so it would only confuse; the note takes its place rather than
 * stacking under it. Narrowed to `checkbox` because that is the only control that renders `disabled`.
 */
function withLockNote(field: SettingField): SettingField {
  if (field.type !== 'checkbox') return field;
  return {
    ...field,
    info: bt(
      'bld.header.cart_locked',
      'Your buyers go through the cart, so the cart icon stays visible. It becomes yours to set once no buy-box in the store adds to the cart — each one must be the cash-on-delivery order form or “Buy now”.',
    ),
  };
}

/**
 * The auto-generated section form (build-plan §6 / §6.6). Renders one `FieldControl` per
 * `SectionSchema.fields` entry, bound to the live `settings[field.id]`, dispatching
 * `updateSectionSettings` with a partial keyed patch on change. It renders ONLY the section's own
 * settings — block management (the block list + Add-block) lives entirely in the LEFT rail tree, never
 * duplicated here (founder ask 2026-06-25).
 *
 * Deep-config infra: fields are first filtered by `visibleWhen` (conditional visibility), then
 * partitioned by `group` — ungrouped fields render directly under the expanded "Settings" group, and
 * each NAMED group renders as its own labelled collapsible. A group whose fields are all hidden is
 * itself omitted, so no empty headers appear.
 */
export function SectionForm({ section }: { section: SectionInstance }) {
  const updateSectionSettings = useBuilderStore((s) => s.updateSectionSettings);
  const selectSection = useBuilderStore((s) => s.selectSection);
  // Can a shopper of this STORE end up in a cart? See `state/cartMode.ts` — locked unless the whole store
  // (the other pages as the backend lists them + this page, live) says no buy-box adds to cart.
  const cartMode = useStoreIsInCartMode();
  const def = sectionRegistry[section.type];
  if (!def) {
    return (
      <div className="bld-inspector__empty">
        {bt(
          'bld.unknown_section',
          "This section type isn't available in the current version, so its settings can't be edited here.",
        )}
      </div>
    );
  }

  const renderField = (field: SettingField) => {
    const locked = isCartLocked(section.type, field, cartMode);
    return (
      <FieldControl
        key={field.id}
        field={locked ? withLockNote(field) : field}
        sectionId={section.id}
        inherited={inheritedValue(field, def.schema.fields, section.settings)}
        // What the header ACTUALLY renders in cart mode, not what the document stored: a page saved
        // `showCart:false` under COD and then switched to card would otherwise show a greyed-OFF switch
        // beside a storefront that displays the icon. The stored value is left untouched — switch the
        // store back to COD and the seller's choice comes back.
        value={locked ? true : section.settings[field.id]}
        disabled={locked}
        onChange={(next: SettingValue | undefined) =>
          updateSectionSettings(section.id, { [field.id]: next as SettingValue })
        }
      />
    );
  };

  // The ONE seam where the inspector holds the section type and the schema together (feature 0051
  // lot 4). Translating here leaves all 57 schema files and all 17 control components untouched, and
  // `visibleWhen` still reads `field.id`, which never moves.
  const groups = groupFields(translateFields(section.type, def.schema.fields)).map(
    ([name, fields]) => [name, fields.filter((f) => isFieldVisible(f, section.settings))] as const,
  );
  const defaultGroup = groups.find(([name]) => name === DEFAULT_GROUP)?.[1] ?? [];
  const namedGroups = groups.filter(
    ([name, fields]) => name !== DEFAULT_GROUP && fields.length > 0,
  );

  return (
    // The node address every control under here can ask for — it is what lets a media upload started in
    // this panel keep running, and keep reporting, after the panel is closed (see `lib/mediaUploads.ts`).
    <FieldTargetProvider target={`section:${section.id}`} reveal={() => selectSection(section.id)}>
      {/* Shopify parity: ungrouped fields render directly under the section title (no "Settings"
          header); each named `group` renders as a FLAT header + thin divider (not an accordion). The
          trailing "Theme settings" + "Custom CSS" accordions were removed: they held nothing but a
          note, repeated under EVERY section, while the real theme settings live in the right rail. */}
      {defaultGroup.length > 0 && <div className="bld-form">{defaultGroup.map(renderField)}</div>}
      {namedGroups.map(([name, fields]) => {
        const device = DEVICE_GROUPS[name];
        return (
          <section
            key={name}
            className={`bld-grp${device ? ' bld-grp--device' : ''}`}
            data-device={device}
          >
            <h3 className="bld-grp__title">
              {device && <DeviceGlyph device={device} />}
              {groupLabel(section.type, name)}
            </h3>
            <div className="bld-form">{fields.map(renderField)}</div>
          </section>
        );
      })}
    </FieldTargetProvider>
  );
}
